'use client'

import { useTranslate } from '@repo/shared/hooks'
import { ValueWithUnit, globalConfig } from '@repo/shared/index'
import { useListProjects } from '@repo/services'
import {
  AspectRatio,
  Button,
  Card,
  Col,
  Icon,
  PriceChangeBadge,
  Row,
  Typography,
} from '@repo/ui/components'
import { useRef, useState } from 'react'
import { cn } from '@repo/ui/lib'
import Image from 'next/image'

type CryptoPriceItem = {
  id: number
  name: string
  faName: string
  symbol: string
  icon: React.ReactNode
  price: number
  priceChange: number
}

export const CryptoInstantPrice = () => {
  const { t } = useTranslate()

  const { data } = useListProjects({
    skip: 0,
    limit: 10,
    kind: 'crypto',
  })
  const tokensData =
    data?.pages.flatMap((page) => page.data?.projects || []) || []

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  const cryptoPrices: CryptoPriceItem[] = tokensData.map((project, index) => ({
    id: project.id ? Number(project.id) : index,
    name: project.displayName || project.token || '',
    faName: project.displayName || project.token || '',
    symbol: project.token || '',
    icon: project.token ? (
      <AspectRatio className='aspect-1/1'>
        <Image
          src={
            project?.logoURL
              ? `${globalConfig.baseImageUrl}${project?.logoURL}`
              : '/images/placeholder.webp'
          }
          alt='Photo of property'
          fill
          sizes='(max-width: 768px) 100vw, 24px'
          className='h-full w-full  rounded-2xs object-cover'
          loading='lazy'
        />
      </AspectRatio>
    ) : (
      <Icon name='CardLine' size='sm' />
    ),
    price: project.price?.priceRange24h?.last || 0,
    priceChange: project.price?.priceRange24h?.deltaPercent || 0,
  }))

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft
      const cardWidth = 280 // Approximate card width
      const newIndex = Math.round(scrollLeft / cardWidth)
      setCurrentIndex(newIndex)
    }
  }

  if (cryptoPrices.length === 0) {
    return null
  }

  return (
    <Card withoutBorder>
      <Col className='gap-xs'>
        <Row className='justify-between items-center'>
          <Typography size='xs' color='text-neutral-surface-100' weight='bold'>
            {t('depositCryptoPage_instant_sell_price_title')}
          </Typography>

          <Row className='justify-center w-fit gap-xs'>
            <Button
              variant='transparent'
              color='neutral'
              size='xs'
              onClick={scrollRight}
              type='button'
              className='rounded-full shrink-0'
            >
              <Icon name='ChevronRight' size='xs' />
            </Button>
            {cryptoPrices.map((_, idx) => (
              <span
                key={idx}
                className={cn(
                  'inline-block rounded-full transition-all bg-neutral-surface-100',
                  idx === currentIndex ? 'w-4 h-1' : 'w-1 h-1 opacity-50',
                )}
                style={{ transition: 'width 0.2s, height 0.2s' }}
              />
            ))}
            <Button
              variant='transparent'
              color='neutral'
              size='xs'
              type='button'
              onClick={scrollLeft}
              className='rounded-full shrink-0'
            >
              <Icon name='ChevronLeft' size='xs' />
            </Button>
          </Row>
        </Row>
        <Row className='gap-md items-center'>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className='flex gap-md overflow-x-auto flex-1 [&::-webkit-scrollbar]:hidden max-w-[600px]'
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {cryptoPrices.map((crypto) => (
              <Card key={crypto.id} className='min-w-[200px] p-sm shrink-0'>
                <Row className='gap-sm'>
                  <Row className='gap-xs w-[24px] '>{crypto.icon}</Row>

                  <Col className='gap-2xs'>
                    <Row className='gap-xs justify-between'>
                      <Typography
                        size='xs'
                        weight='regular'
                        color='text-neutral-surface-100'
                      >
                        {crypto.faName}
                      </Typography>
                      <PriceChangeBadge value={crypto.priceChange} size='sm' />
                    </Row>

                    <Row className='gap-xs  justify-between'>
                      <Typography size='xs' color='text-neutral-surface-60'>
                        {crypto.symbol}
                      </Typography>
                      <ValueWithUnit
                        value={crypto.price}
                        scale={10}
                        unit='TMN'
                        valueSize='xs'
                        valueWeight='bold'
                        valueColor='text-neutral-surface-100'
                        decimalPlaces={0}
                      />
                    </Row>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        </Row>
      </Col>
    </Card>
  )
}
